import type { Card, Transaction } from '@asia/shared';
import { rpc } from './ws';

/** A card product on sale, priced and merged with the operator's settings. */
export interface CardTemplate {
  templateId: number;
  name: string;
  country: string | null;
  paymentSystem: string | null;
  type: string | null;
  priceUsd: number;
  issueNetworkFeeHidden: boolean;
  providerTopupFeePct: number;
  /** Unified customer-facing top-up fee (% of credited amount). */
  topupFeePct: number;
  /** @deprecated Prefer topupFeePct. */
  projectTopupFeeUsd?: number;
  minTopupUsd: number | null;
  maxTopupUsd: number | null;
  /** Banana activate_min_limit — included in issue total when requiresInitialDeposit. */
  minDepositUsd: number | null;
  limitPerTxUsd: number | null;
  limitPerDayUsd: number | null;
  limitPerMonthUsd: number | null;
  needsActivation: boolean;
  requiresInitialDeposit: boolean;
  supportsApplePay: boolean;
  supportsGooglePay: boolean;
  /** Only template #25 — user must enter email before issue. */
  requiresEmail?: boolean;
  /** A pre-issued card can be claimed without registering a new Banana user. */
  poolAvailable?: boolean;
}

/** A challenge waiting on the user: a payment 3DS, or adding the card to a
 *  phone wallet. `otp` is present when the provider sends a code to type. */
export interface CardChallenge {
  id: string;
  kind: 'payment' | 'wallet' | null;
  method: string | null;
  otp: string | null;
  amount: string | null;
}

export interface ApiCard {
  id: string;
  label: string | null;
  name: string | null;
  templateId: number | null;
  paymentSystem: string | null;
  last4: string | null;
  expiry: string | null;
  currency: string;
  status: 'pending' | 'active' | 'frozen' | 'closed' | 'failed';
  balanceUsd: number;
  supportsApplePay: boolean;
  supportsGooglePay: boolean;
  minTopupUsd: number | null;
  maxTopupUsd: number | null;
  pendingChallenge: CardChallenge | null;
  createdAt: string;
}

export interface CardTx {
  id: string;
  type: string;
  merchant: string | null;
  mcc: string | null;
  amount: string;
  currency: string;
  localAmount: string | null;
  localCurrency: string | null;
  feeUsd: string;
  status: string;
  createdAt: string;
}

export interface IssueQuote {
  templateId: number;
  name: string;
  priceUsd: number;
  token: 'USDT';
  /** Where the issuance fee must be sent — an ordinary USDT transfer. */
  payTo: string;
  /** False against the provider's test environment: the card isn't real, so no
   *  real balance is charged for it. */
  paymentRequired: boolean;
  /** A previously broadcast, unclaimed payment for this template. */
  resumeTransferId: string | null;
  requiresInitialDeposit: boolean;
  minTopupUsd: number | null;
}

/**
 * Server card → the shape the card components already render. The number and
 * CVV stay empty: the face masks them until the user asks for the details,
 * which fetches them live behind a PIN or biometric check.
 */
export function toDisplayCard(c: ApiCard): Card {
  const [mm, yy] = (c.expiry ?? '').split('/');
  return {
    id: c.id,
    label: c.label ?? c.name ?? 'Card',
    brand: /master/i.test(c.paymentSystem ?? '') ? 'mastercard' : 'visa',
    last4: c.last4 ?? '••••',
    balanceUsd: c.balanceUsd,
    status:
      c.status === 'frozen'
        ? 'frozen'
        : c.status === 'closed'
          ? 'deleted'
          : c.status === 'pending' || c.status === 'failed'
            ? 'pending'
            : 'active',
    supportsApplePay: c.supportsApplePay,
    supportsGooglePay: c.supportsGooglePay,
    theme: 'light',
    expMonth: Number(mm) || 0,
    expYear: Number(yy) || 0,
  };
}

/** Money that came off the card reads as an outgoing row; a refund or a top-up
 *  reads as incoming. Merchant details ride along so the row can name the shop. */
export function cardTxToTransaction(t: CardTx): Transaction {
  const topup = t.type === 'recharge';
  const incoming = topup || t.type === 'refund';
  const amount = Math.abs(Number(t.amount) || 0);
  return {
    id: t.id,
    type: topup ? 'topup' : incoming ? 'receive' : 'send',
    status: t.status === 'declined' ? 'failed' : 'confirmed',
    coin: 'USDT',
    chain: 'TRON',
    amount: incoming ? amount : -amount,
    amountUsd: amount,
    feeUsd: Number(t.feeUsd) || 0,
    date: t.createdAt,
    merchant: t.merchant ?? undefined,
    mcc: t.mcc ?? undefined,
    storeAmount: t.localAmount ? Number(t.localAmount) : undefined,
    storeCurrency: t.localCurrency ?? undefined,
  };
}

export const fetchCardTemplates = () =>
  rpc<{ templates: CardTemplate[] }>('cards.templates').then((r) => r.templates);

export type CardAccountStatus = 'none' | 'pending' | 'approved' | 'rejected';

export const cardAccountStatusApi = (templateId: number) =>
  rpc<{ registered: boolean; status: CardAccountStatus; rejectionMessage: string | null; email: string | null }>(
    'cards.accountStatus',
    { templateId },
  );

/** Register the Banana cardholder before payment (required for template #25). */
export const ensureCardAccountApi = (
  templateId: number,
  opts?: { email?: string; waitForApproval?: boolean; forceReregister?: boolean },
) =>
  rpc<{ registered: boolean; status: CardAccountStatus; rejectionMessage: string | null; email: string | null }>(
    'cards.accountEnsure',
    {
      templateId,
      waitForApproval: opts?.waitForApproval ?? false,
      ...(opts?.email ? { email: opts.email } : {}),
      ...(opts?.forceReregister ? { forceReregister: true } : {}),
    },
    opts?.waitForApproval ? 60_000 : undefined,
  );

export const fetchCards = () => rpc<{ cards: ApiCard[] }>('cards.list').then((r) => r.cards);

export const fetchCard = (id: string) => rpc<{ card: ApiCard }>('cards.get', { id }).then((r) => r.card);

export interface CardTxPage {
  transactions: CardTx[];
  /** ISO timestamp cursor for the next (older) page, or null at the end. */
  nextBefore: string | null;
}

/** One page of card transactions, newest first. `before` is a cursor from a
 *  previous page's nextBefore for infinite scroll. */
export const fetchCardTransactions = (id: string, before?: string) =>
  rpc<CardTxPage>('cards.transactions', before ? { id, before } : { id });

export const issueQuoteApi = (templateId: number) => rpc<IssueQuote>('cards.issueQuote', { templateId });

export interface IssueStatus {
  intentId: string;
  templateId: number | null;
  status: 'pending' | 'failed';
  failureReason: string | null;
  paymentTransferId: string | null;
  updatedAt: string;
}

export const issueStatusApi = () =>
  rpc<{ issue: IssueStatus | null }>('cards.issueStatus').then((r) => r.issue);

/** Issue a card, paid for by an already-confirmed USDT transfer to `payTo`. */
export const issueCardApi = (params: {
  templateId: number;
  paymentTransferId?: string;
  idempotencyKey: string;
  reauthToken?: string;
  email?: string;
}) => rpc<{ intentId: string; status: string; card: ApiCard | null }>('cards.issue', params);

export interface TopupQuote {
  cardId: string;
  amountUsd: number;
  /** Unified customer-facing top-up fee (% of credited amount). */
  topupFeePct: number;
  /** Absolute top-up fee in USD (= amount × topupFeePct). */
  topupFeeUsd: number;
  /** @deprecated Prefer topupFeeUsd. */
  providerFeeUsd: number;
  /** @deprecated Prefer topupFeeUsd. */
  projectFeeUsd: number;
  /** Banana's recharge cost (P&L only — not charged on top of topupFeeUsd). */
  providerCostUsd?: number;
  totalUsd: number;
  token: 'USDT';
  payTo: string;
  paymentRequired: boolean;
  resumeTransferId: string | null;
  minTopupUsd: number | null;
  maxTopupUsd: number | null;
}

export const topupQuoteApi = (id: string, amount?: string) =>
  rpc<TopupQuote>('cards.topupQuote', { id, amount });

/** Move money onto a card, paid for by an already-confirmed USDT transfer. */
export const topupCardApi = (params: {
  id: string;
  amount: string;
  paymentTransferId?: string;
  idempotencyKey: string;
  reauthToken?: string;
}) => rpc<{ intentId: string; status: string }>('cards.topup', params);

/** Approve a pending OOB 3DS challenge (Banana `/card/confirm_3ds`). */
export const confirmChallengeApi = (id: string, reauthToken?: string) =>
  rpc<{ card: ApiCard }>('cards.confirmChallenge', { id, reauthToken }).then((r) => r.card);

/** Clear a sticky OTP / wallet-verification prompt (code is entered elsewhere). */
export const dismissChallengeApi = (id: string) =>
  rpc<{ card: ApiCard }>('cards.dismissChallenge', { id }).then((r) => r.card);

export interface CardSecrets {
  number: string;
  cvv: string;
  expiry: string;
}

/** SENSITIVE — the live card number and CVV. Never persist or log the result. */
export const cardDetailsApi = (id: string, reauthToken?: string) =>
  rpc<CardSecrets>('cards.details', { id, reauthToken });

/** @deprecated Prefer `cardDetailsApi` — Banana rate-limits get_info per card. */
export const allCardDetailsApi = (reauthToken?: string) =>
  rpc<{ details: Record<string, CardSecrets> }>('cards.allDetails', { reauthToken }).then((r) => r.details);

export const renameCardApi = (id: string, label: string | null) =>
  rpc<{ card: ApiCard }>('cards.rename', { id, label }).then((r) => r.card);

export const setCardStatusApi = (id: string, action: 'freeze' | 'unfreeze' | 'close', reauthToken?: string) =>
  rpc<{ card: ApiCard }>('cards.setStatus', { id, action, reauthToken }).then((r) => r.card);

export const removeCardApi = (id: string, reauthToken?: string) =>
  rpc<{ ok: boolean }>('cards.remove', { id, reauthToken });
